import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

/**
 * Lightweight confirm dialog for destructive admin actions.
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *   setConfirm({ title, message, tone:'danger', onConfirm });
 *   <AdminConfirm state={confirm} onClose={()=>setConfirm(null)} />
 */
const AdminConfirm = ({ state, onClose }) => {
  if (!state) return null;
  const { title, message, tone = 'danger', confirmLabel = '확인', onConfirm } = state;

  const handleConfirm = async () => {
    try { await onConfirm?.(); } finally { onClose?.(); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="admin-confirm">
      <div className="w-full max-w-md bg-white dark:bg-[#141414] rounded-2xl shadow-xl border border-black/10 dark:border-white/10">
        <div className="flex items-start justify-between p-5 border-b border-black/5 dark:border-white/10">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-full ${tone === 'danger' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'}`}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <h3 className="font-black text-lg" data-testid="admin-confirm-title">{title}</h3>
              {message && <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-testid="admin-confirm-message">{message}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md hover:bg-black/5 dark:hover:bg-white/10" aria-label="닫기">
            <X size={16} />
          </button>
        </div>
        <div className="flex justify-end gap-2 p-4">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-bold bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20"
            data-testid="admin-confirm-cancel">취소</button>
          <button type="button" onClick={handleConfirm}
            className={`px-4 py-2 rounded-full text-sm font-bold text-white ${tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#FF3B30] hover:bg-[#D32F2F]'}`}
            data-testid="admin-confirm-ok">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

export default AdminConfirm;
